"use client"

import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import AuthLayout from './AuthLayout'
import { useVerifyOtp } from '../../lib/api'
import { getPendingUserId } from '../../lib/api/services/auth'

const ConfirmLogin = () => {
  const [codes, setCodes] = useState(['', '', '', '', '', ''])
  const [timeLeft, setTimeLeft] = useState(300) // 5 minutes countdown
  const inputRefs = useRef<(HTMLInputElement | null)[]>([])
  const navigate = useNavigate()
  const location = useLocation()
  const verifyOtpMutation = useVerifyOtp()

  const from = (location.state as any)?.from?.pathname || '/admin'
  const userEmail = (location.state as any)?.email || 'your email'

  // Countdown timer
  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [timeLeft])

  // Check if we have a pending user ID, if not redirect back to login
  useEffect(() => {
    const pendingUserId = getPendingUserId()
    if (!pendingUserId) {
      navigate('/login', { replace: true })
    }
  }, [navigate])

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleCodeChange = (index: number, value: string) => {
    // Only allow digits
    if (!/^\d?$/.test(value)) return

    const newCodes = [...codes]
    newCodes[index] = value
    setCodes(newCodes)

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Handle backspace
    if (e.key === 'Backspace' && !codes[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const otp = codes.join('')
    if (otp.length !== 6) {
      return
    }

    const pendingUserId = getPendingUserId()
    if (!pendingUserId) {
      navigate('/login', { replace: true })
      return
    }

    console.log("🔐 OTP verification attempt:", { pendingUserId, otp })

    verifyOtpMutation.mutate(
      { userId: pendingUserId, otp },
      {
        onSuccess: (response) => {
          console.log("📝 OTP verification response:", response)
          if (response.success) {
            console.log("✅ OTP verification successful")
            console.log("🏪 Checking auth data after OTP...")
            console.log("Token:", sessionStorage.getItem('auth_token'))
            console.log("User:", sessionStorage.getItem('current_user'))

            setTimeout(() => {
              console.log("🔄 Navigating after OTP to:", from)
              navigate(from, { replace: true })
            }, 100)
          }
        },
        onError: (error) => {
          console.error("❌ OTP verification error:", error)
          // Clear the codes on error
          setCodes(['', '', '', '', '', ''])
          inputRefs.current[0]?.focus()
        }
      }
    )
  }

  const handleResendCode = async () => {
    try {
      // TODO: Implement resend OTP logic
      await new Promise(resolve => setTimeout(resolve, 500)) // Simulate API call
      setTimeLeft(300) // Reset countdown to 5 minutes
      setCodes(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } catch (error) {
      console.error('Failed to resend code:', error)
    }
  }

  const handleBackToLogin = () => {
    navigate('/login')
  }

  return (
    <AuthLayout>
      {/* Form Header */}
      <div className="text-center mb-6 sm:mb-8">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2 font-satoshi">
          Confirm Your Login
        </h2>
        <p className="text-sm sm:text-base text-gray-600 font-satoshi">
          A verification code has been sent to {userEmail}.
        </p>
      </div>

      {/* Verification Form */}
      <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {verifyOtpMutation.error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm font-satoshi">
            {verifyOtpMutation.error.message || 'Invalid verification code. Please try again.'}
          </div>
        )}

        {/* Code Input Fields */}
        <div className="flex justify-center space-x-1 sm:space-x-2">
          {codes.map((code, index) => (
            <input
              key={index}
              ref={el => { inputRefs.current[index] = el }}
              type="text"
              value={code}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              maxLength={1}
              className="w-10 h-10 sm:w-12 sm:h-12 text-center text-lg sm:text-xl font-bold border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-forestGreen-100 focus:border-transparent outline-none transition-colors bg-white font-satoshi"
            />
          ))}
        </div>

        {/* Verify Button */}
        <button
          type="submit"
          disabled={verifyOtpMutation.isPending || codes.join('').length !== 6}
          className={`w-full font-medium py-2.5 sm:py-3 px-4 rounded-lg transition-colors font-satoshi disabled:bg-gray-400 text-sm sm:text-base ${
            verifyOtpMutation.isPending
              ? 'bg-gray-400 cursor-not-allowed text-white'
              : 'bg-forestGreen-100 hover:bg-green-700 text-white'
          }`}
        >
          {verifyOtpMutation.isPending ? (
            <div className="flex items-center justify-center">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
              Verifying...
            </div>
          ) : (
            'Verify Code'
          )}
        </button>

        {/* Resend Code */}
        <div className="text-center space-y-2">
          <p className="text-sm text-gray-600 font-satoshi">
            Didn't receive the code?{' '}
            {timeLeft > 0 ? (
              <span className="text-gray-500">Resend code ({formatTime(timeLeft)})</span>
            ) : (
              <button
                type="button"
                onClick={handleResendCode}
                className="text-teal-600 hover:text-teal-700 font-medium"
              >
                Resend code
              </button>
            )}
          </p>

          <button
            type="button"
            onClick={handleBackToLogin}
            className="w-full text-sm text-gray-600 hover:text-gray-800 font-satoshi mt-2"
          >
            Back to login
          </button>
        </div>
      </form>
    </AuthLayout>
  )
}

export default ConfirmLogin